import { Thermometer } from "lucide-react";
import type { Reading } from "@spf/contracts";
import { Card } from "../Card.js";
import { formatUnit } from "../../lib/format.js";

/**
 * The design's thermal health map, on the three sensors this inverter has.
 *
 * The design lists a BMS cell-pack temperature, a DC/DC temperature and a fan
 * state, each with a hardcoded verdict beside it ("Optimal", "Nominal",
 * "Silent"). Two of the three readings exist here; the cell-pack one does not,
 * because the battery reports no temperature of its own to this inverter.
 *
 * The verdicts are gone entirely. Whether 43.7 °C is optimal is a rule about
 * this hardware, and section 3 puts rules on the server -- there is no
 * threshold in the contracts, so there is nothing this file may assert.
 */
export function ThermalCard({ reading }: { reading: Reading }) {
  return (
    <Card title="Thermal">
      <p className="mb-4 flex items-start gap-2 text-xs leading-relaxed text-ink-muted">
        <Thermometer size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-accent-2-ink" />
        <span>What the inverter reports about its own heat. The battery reports none.</span>
      </p>

      <dl className="space-y-4">
        <Row label="Inverter" value={formatUnit(reading.inverterTempC, "°C")} />
        <Row label="Bi-directional DC-DC" value={formatUnit(reading.dcdcTempC, "°C")} />
        <Row label="Cooling fan" value={formatUnit(reading.fanSpeedPct, "%")} />
      </dl>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd className="tabular text-sm font-bold text-ink">{value}</dd>
    </div>
  );
}
